import { Box, Link, MenuItem, Select, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { APP_LINK_STATE } from '../Home/navRoutes';
import { COLORS, CARD_SHADOW } from './tokens';

// Breadcrumb bar (DESIGN.md → Breadcrumb bar; Insight: MVP Paper file): a 48px
// white strip across the top of the content area with the card shadow. Links
// are primary at Semibold; the `/` separators and the current page use
// text-secondary at Medium. The current page is not a link.
const BAR_FONT = '"Nunito Sans", "Helvetica", "Arial", sans-serif';
const CRUMB_TEXT = { fontFamily: BAR_FONT, fontSize: '16px', lineHeight: '24px', whiteSpace: 'nowrap' } as const;

export type Crumb = { label: string; to?: string; onClick?: () => void; state?: Record<string, unknown> };
export type RosterEntry = { id: string; name: string };

function PagerButton({
  children,
  disabled,
  onClick,
  label,
}: {
  children: React.ReactNode;
  disabled: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <Box
      component="button"
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 38,
        height: 38,
        borderRadius: 1.5,
        border: `1px solid ${COLORS.hairline}`,
        bgcolor: '#FFFFFF',
        color: COLORS.ink,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        p: 0,
        transition: 'background-color 0.12s ease',
        '&:hover': { bgcolor: disabled ? '#FFFFFF' : '#F0F0F5' },
      }}
    >
      {children}
    </Box>
  );
}

export function TopBar({
  crumbs,
  index = -1,
  total = 0,
  roster = [],
  onSelect,
  onPrev,
  onNext,
}: {
  crumbs: Crumb[];
  index?: number;
  total?: number;
  roster?: RosterEntry[];
  onSelect?: (id: string) => void;
  onPrev?: () => void;
  onNext?: () => void;
}) {
  const hasPager = index >= 0 && total > 0;
  const currentId = hasPager ? roster[index]?.id : undefined;

  return (
    <Box
      sx={{
        minHeight: 48,
        px: '24px',
        py: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        bgcolor: '#FFFFFF',
        boxShadow: CARD_SHADOW,
        position: 'relative',
        zIndex: 1,
      }}
    >
      <Box
        component="nav"
        aria-label="Breadcrumb"
        sx={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap', minWidth: 0 }}
      >
        {crumbs.map((c, i) => {
          const last = i === crumbs.length - 1;
          const clickable = Boolean(c.onClick);
          return (
            <Box key={`${c.label}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {i > 0 && (
                <Typography component="span" aria-hidden="true" sx={{ ...CRUMB_TEXT, fontWeight: 500, color: COLORS.muted }}>
                  /
                </Typography>
              )}
              {c.to ? (
                <Link
                  component={RouterLink}
                  to={c.to}
                  state={c.state ? { ...APP_LINK_STATE, ...c.state } : APP_LINK_STATE}
                  underline="hover"
                  sx={{
                    ...CRUMB_TEXT,
                    fontWeight: 600,
                    color: COLORS.navy,
                    '&:focus-visible': {
                      outline: `2px solid ${COLORS.navy}`,
                      outlineOffset: 2,
                      borderRadius: 0.5,
                    },
                  }}
                >
                  {c.label}
                </Link>
              ) : (
                <Typography
                  component="span"
                  aria-current={last ? 'page' : undefined}
                  data-testid={last ? 'crumb-current' : undefined}
                  onClick={c.onClick}
                  role={clickable ? 'button' : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onKeyDown={
                    clickable
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            c.onClick?.();
                          }
                        }
                      : undefined
                  }
                  sx={{
                    ...CRUMB_TEXT,
                    fontWeight: clickable ? 600 : 500,
                    color: clickable ? COLORS.navy : COLORS.muted,
                    cursor: clickable ? 'pointer' : 'default',
                    '&:hover': clickable ? { textDecoration: 'underline' } : undefined,
                    '&:focus-visible': clickable
                      ? { outline: `2px solid ${COLORS.navy}`, outlineOffset: 2, borderRadius: 0.5 }
                      : undefined,
                  }}
                >
                  {c.label}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      {hasPager && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: COLORS.muted, mr: 0.5 }}>
            {index + 1} of {total}
          </Typography>
          <PagerButton label="Previous student" disabled={index <= 0} onClick={() => onPrev?.()}>
            <ChevronLeftIcon sx={{ fontSize: 20 }} />
          </PagerButton>
          <Select
            size="small"
            value={currentId ?? ''}
            onChange={(e) => onSelect?.(String(e.target.value))}
            sx={{
              minWidth: 200,
              bgcolor: '#FFFFFF',
              fontSize: 14,
              fontWeight: 600,
              color: COLORS.ink,
              borderRadius: 1.5,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.hairline },
            }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
          >
            {roster.map((r) => (
              <MenuItem key={r.id} value={r.id} sx={{ fontSize: 14 }}>
                {r.name}
              </MenuItem>
            ))}
          </Select>
          <PagerButton label="Next student" disabled={index >= total - 1} onClick={() => onNext?.()}>
            <ChevronRightIcon sx={{ fontSize: 20 }} />
          </PagerButton>
        </Box>
      )}
    </Box>
  );
}
